// Export & AI Context: turns journal entries into plain text, Markdown or JSON.
// Plain text with the default field groups is the original Export format and must stay byte-for-byte stable.
import { isTapeReading } from './tradeHelpers';
import { formatContextValue, getMindsetExecutionRows } from './journalContext';
import { getEntryDuration } from './tradeDuration';

export const EXPORT_FORMATS = [
  { value: 'text', label: 'Plain text', short: 'Text', extension: 'txt', mime: 'text/plain' },
  { value: 'markdown', label: 'Markdown', short: 'Markdown', extension: 'md', mime: 'text/markdown' },
  { value: 'json', label: 'JSON', short: 'JSON', extension: 'json', mime: 'application/json' },
];

export const EXPORT_GROUPS = [
  { id: 'details', label: 'Trade details' },
  { id: 'environment', label: 'Trade environment' },
  { id: 'mindset', label: 'Mindset & execution' },
  { id: 'notes', label: 'Notes' },
  { id: 'images', label: 'Screenshot links' },
];

export const DEFAULT_EXPORT_GROUPS = { details: true, environment: true, mindset: true, notes: true, images: false };
export const DEFAULT_EXPORT_OPTIONS = { format: 'text', groups: DEFAULT_EXPORT_GROUPS };

const STORAGE_KEY = 'bandz.contextExport.v1';
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Keys rendered elsewhere (or internal); anything else on an entry is a legacy custom field.
const EXCLUDED_KEYS = ['title', 'tickerTraded', 'entryTime', 'exitTime', 'duration', 'pnl', 'rr',
  'tradeSession', 'economicRelease', 'poi', 'pdArray', 'target', 'notes', 'accountName',
  'isDeposit', 'isPayout', 'isTapeReading', 'created', 'year', 'month', 'day',
  'entryYear', 'entryMonth', 'entryDay', 'exitYear', 'exitMonth', 'exitDay', 'tradingStyle',
  'screenshots', 'accountBalance', 'id', 'tapeReading', 'accountId', 'dayOfWeek', 'tradeRating',
  'mindsetExecution', 'isBalanceAdjustment', 'adjustmentTarget', 'isResetExcluded'];

export function normalizeExportOptions(raw) {
  const format = EXPORT_FORMATS.some((f) => f.value === raw?.format) ? raw.format : DEFAULT_EXPORT_OPTIONS.format;
  const groups = { ...DEFAULT_EXPORT_GROUPS };
  if (raw?.groups && typeof raw.groups === 'object') {
    EXPORT_GROUPS.forEach(({ id }) => {
      if (typeof raw.groups[id] === 'boolean') groups[id] = raw.groups[id];
    });
  }
  return { format, groups };
}

export function loadExportOptions() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return normalizeExportOptions(stored ? JSON.parse(stored) : null);
  } catch (error) {
    return normalizeExportOptions(null);
  }
}

export function saveExportOptions(options) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizeExportOptions(options)));
  } catch (error) {
    // Storage can be blocked (private mode); the choice just won't be remembered.
  }
}

export function isDefaultExportOptions(options) {
  const normalized = normalizeExportOptions(options);
  return normalized.format === DEFAULT_EXPORT_OPTIONS.format
    && EXPORT_GROUPS.every(({ id }) => normalized.groups[id] === DEFAULT_EXPORT_GROUPS[id]);
}

export function getExportFormat(value) {
  return EXPORT_FORMATS.find((f) => f.value === value) || EXPORT_FORMATS[0];
}

/** "9/30/2026"-style label used for the `=== date ===` headers ('Unknown date' when unparseable). */
export function getEntryDateLabel(entry) {
  if (entry?.month && entry?.day) {
    const year = entry.year || new Date().getFullYear();
    const date = new Date(year, parseInt(entry.month, 10) - 1, parseInt(entry.day, 10));
    if (!Number.isNaN(date.getTime())) return date.toLocaleDateString();
  }
  if (typeof entry?.created === 'string') {
    const lastDashIndex = entry.created.lastIndexOf('-');
    const cleanTimestamp = lastDashIndex > 10 ? entry.created.substring(0, lastDashIndex) : entry.created;
    const date = new Date(cleanTimestamp);
    if (!Number.isNaN(date.getTime())) return date.toLocaleDateString();
  }
  return 'Unknown date';
}

function getEntryKind(entry) {
  if (entry.isBalanceAdjustment) return 'balance adjustment';
  if (entry.isDeposit) return 'deposit';
  if (entry.isPayout) return 'payout';
  return isTapeReading(entry) ? 'tape reading' : 'trade';
}

const padDate = (y, m, d) => `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

function getDayOfWeek(entry) {
  if (entry.month && entry.day) {
    const year = entry.year || new Date().getFullYear();
    const date = new Date(year, parseInt(entry.month, 10) - 1, parseInt(entry.day, 10));
    if (!Number.isNaN(date.getTime())) return DAYS[date.getDay()];
  }
  if (typeof entry.created === 'string') {
    const lastDashIndex = entry.created.lastIndexOf('-');
    if (lastDashIndex > 10) {
      const date = new Date(entry.created.substring(0, lastDashIndex));
      if (!Number.isNaN(date.getTime())) return DAYS[date.getDay()];
    }
  }
  return 'N/A';
}

/**
 * One row per output line, tagged with the field group it belongs to:
 * `{ group, label, value }`, or `{ group: 'summary', text }` for deposits/payouts/adjustments.
 */
export function entryToRows(entry, fieldDefinitions = { tradeEnv: [] }) {
  const toYesNo = (value) => {
    if (value === true || value === 'true') return 'Yes';
    if (value === false || value === 'false') return 'No';
    return 'N/A';
  };
  const toAligned = (value) => {
    if (value === 'above' || value === true || value === 'true') return 'Aligned';
    if (value === 'below' || value === false || value === 'false') return 'Not Aligned';
    return 'N/A';
  };
  const formatFieldValue = (value, fieldType) => {
    if (value === undefined || value === null || value === '') return 'N/A';
    if (fieldType === 'text' || fieldType === 'dropdown') return formatContextValue(value);
    if (fieldType === 'yesNoNA') return toYesNo(value);
    return typeof value === 'boolean' || value === 'true' || value === 'false'
      ? toYesNo(value)
      : formatContextValue(value);
  };

  const rows = [];
  const add = (group, label, value) => rows.push({ group, label, value: String(value) });
  if (entry.accountName) add('account', 'Account', entry.accountName);

  if (entry.isBalanceAdjustment) {
    rows.push({ group: 'summary', text: `Balance Adjustment: ${Number(entry.pnl) >= 0 ? '+' : '-'}$${Math.abs(entry.pnl || 0)} - New Account Balance: $${entry.adjustmentTarget ?? entry.accountBalance ?? 0} - ${entry.notes || '(blank)'}` });
    return rows;
  }
  if (entry.isDeposit) {
    rows.push({ group: 'summary', text: `Account Balance: $${entry.accountBalance || 0} - ${entry.notes || '(blank)'}` });
    return rows;
  }
  if (entry.isPayout) {
    rows.push({ group: 'summary', text: `Payout: $${Math.abs(entry.pnl || 0)} - Account Balance: $${entry.accountBalance || 0} - ${entry.notes || '(blank)'}` });
    return rows;
  }

  // Trades and tape reading share one layout.
  add('details', 'Title', entry.title || 'N/A');
  add('details', 'Rating', entry.tradeRating || 'N/A');
  add('details', 'Ticker', entry.tickerTraded || 'N/A');
  if (entry.tradingStyle === 'swing' && entry.year && entry.month && entry.day) {
    add('details', 'Log Date', padDate(entry.year, entry.month, entry.day));
  }
  if (entry.tradingStyle === 'swing' && entry.entryYear && entry.entryMonth && entry.entryDay) {
    add('details', 'Entry Date', padDate(entry.entryYear, entry.entryMonth, entry.entryDay));
  }
  add('details', 'Entry Time', entry.entryTime || 'N/A');
  if (entry.tradingStyle === 'swing' && entry.exitYear && entry.exitMonth && entry.exitDay) {
    add('details', 'Exit Date', padDate(entry.exitYear, entry.exitMonth, entry.exitDay));
  }
  add('details', 'Exit Time', entry.exitTime || 'N/A');
  add('details', 'Duration', getEntryDuration(entry) || 'N/A');
  if (!isTapeReading(entry)) {
    add('details', 'P&L', entry.pnl !== undefined && entry.pnl !== '' ? entry.pnl : 'N/A');
    add('details', 'R:R', entry.rr !== undefined && entry.rr !== '' ? entry.rr : 'N/A');
  }
  add('details', 'Trade Session', entry.tradeSession || 'N/A');
  add('details', 'Economic Release', entry.economicRelease || 'N/A');
  add('details', 'Screenshots Attached', Array.isArray(entry.screenshots) ? entry.screenshots.length : 0);
  add('details', 'Day of the Week', getDayOfWeek(entry));

  const tradeEnv = fieldDefinitions?.tradeEnv || [];
  tradeEnv.filter((field) => field.enabled).forEach((field) => {
    const value = entry[field.id];
    if (value === undefined) return;
    const formatted = field.id === 'aboveBelow0000' || field.id === 'aboveBelow0830'
      ? toAligned(value)
      : formatFieldValue(value, field.fieldType || 'yesNoNA');
    add('environment', field.label, formatted);
  });

  getMindsetExecutionRows(entry).forEach((row) => add('mindset', row.label, row.value));

  // Legacy custom fields that aren't in the current definitions.
  const definedFieldIds = new Set(tradeEnv.map((f) => f.id));
  Object.keys(entry).forEach((key) => {
    if (!EXCLUDED_KEYS.includes(key) && !definedFieldIds.has(key)
        && entry[key] !== undefined && entry[key] !== null && entry[key] !== '') {
      add('environment', key, formatContextValue(entry[key]));
    }
  });

  if (entry.pdArray && typeof entry.pdArray === 'string' && entry.pdArray.trim() !== '') {
    add('details', 'What PD array did you enter off of', entry.pdArray);
  }
  if (entry.target && typeof entry.target === 'string' && entry.target.trim() !== '') {
    add('details', 'Target', entry.target);
  }
  add('notes', 'Notes', entry.notes || 'N/A');
  return rows;
}

function getScreenshotLinks(entry) {
  return (Array.isArray(entry.screenshots) ? entry.screenshots : [])
    .filter((url) => typeof url === 'string' && /^https?:\/\//i.test(url));
}

const isIncluded = (row, groups) => row.group === 'account' || row.group === 'summary' || groups[row.group] !== false;

/** Plain-text body for one entry (no date header). Default groups reproduce the original Export text. */
export function entryToText(entry, fieldDefinitions = { tradeEnv: [] }, groups = DEFAULT_EXPORT_GROUPS) {
  const lines = entryToRows(entry, fieldDefinitions)
    .filter((row) => isIncluded(row, groups))
    .map((row) => row.text ?? `${row.label}: ${row.value}`);
  if (groups.images) {
    getScreenshotLinks(entry).forEach((url, index) => lines.push(`Screenshot ${index + 1}: ${url}`));
  }
  return lines.join('\n');
}

const GROUP_HEADINGS = { details: 'Trade details', environment: 'Trade environment', mindset: 'Mindset & execution' };
const oneLine = (value) => String(value).replace(/\s*\n\s*/g, ' ');

function entryToMarkdown(entry, fieldDefinitions, groups) {
  const rows = entryToRows(entry, fieldDefinitions).filter((row) => isIncluded(row, groups));
  const out = [`## ${getEntryDateLabel(entry)}`];
  rows.filter((row) => row.group === 'account').forEach((row) => out.push(`**${row.label}:** ${oneLine(row.value)}`));
  rows.filter((row) => row.group === 'summary').forEach((row) => out.push(oneLine(row.text)));
  ['details', 'environment', 'mindset'].forEach((group) => {
    const groupRows = rows.filter((row) => row.group === group);
    if (groupRows.length === 0) return;
    out.push(`### ${GROUP_HEADINGS[group]}\n${groupRows.map((row) => `- **${row.label}:** ${oneLine(row.value)}`).join('\n')}`);
  });
  const notes = rows.find((row) => row.group === 'notes');
  if (notes) out.push(`### Notes\n${notes.value}`);
  if (groups.images) {
    const links = getScreenshotLinks(entry);
    if (links.length > 0) out.push(`### Screenshots\n${links.map((url, i) => `- [Screenshot ${i + 1}](${url})`).join('\n')}`);
  }
  return out.join('\n\n');
}

function entryToJson(entry, fieldDefinitions, groups) {
  const rows = entryToRows(entry, fieldDefinitions);
  const record = { date: getEntryDateLabel(entry), type: getEntryKind(entry) };
  if (entry.accountName) record.account = entry.accountName;
  const summary = rows.find((row) => row.group === 'summary');
  if (summary) {
    record.summary = summary.text;
    return record;
  }
  ['details', 'environment', 'mindset'].forEach((group) => {
    if (!groups[group]) return;
    record[group] = Object.fromEntries(rows.filter((row) => row.group === group).map((row) => [row.label, row.value]));
  });
  if (groups.notes) record.notes = entry.notes || '';
  if (groups.images) record.screenshots = getScreenshotLinks(entry);
  return record;
}

/**
 * Builds the export for `entries` (already filtered/sorted). Plain text matches the original format:
 * `=== date ===` header per entry, blank line between entries. Callers trim the combined result.
 */
export function formatContextExport(entries = [], { format = 'text', groups = DEFAULT_EXPORT_GROUPS, fieldDefinitions = { tradeEnv: [] } } = {}) {
  const safeGroups = normalizeExportOptions({ groups }).groups;
  if (format === 'json') {
    return JSON.stringify(entries.map((entry) => entryToJson(entry, fieldDefinitions, safeGroups)), null, 2);
  }
  if (format === 'markdown') {
    return entries.map((entry) => entryToMarkdown(entry, fieldDefinitions, safeGroups)).join('\n\n---\n\n');
  }
  return entries
    .map((entry) => `=== ${getEntryDateLabel(entry)} ===\n${entryToText(entry, fieldDefinitions, safeGroups)}`)
    .join('\n\n');
}
